import React, { useState, useEffect } from 'react';
import { getQuestionnaireResponses, saveQuestionnaireResponses } from '../../services/api';
import './OverridePanel.css';

const OverridePanel = ({ valuationId, onSave }) => {
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [changes, setChanges] = useState({});

  useEffect(() => {
    const loadResponses = async () => {
      try {
        setLoading(true);
        const data = await getQuestionnaireResponses(valuationId);
        setResponses(data);

        // Expand risk assessment by default
        if (data.sections?.risk_assessment) {
          setExpandedSections({ risk_assessment: true });
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load questionnaire responses');
        setLoading(false);
      }
    };

    if (valuationId) {
      loadResponses();
    }
  }, [valuationId]);

  const toggleSection = (sectionCode) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionCode]: !prev[sectionCode]
    }));
  };

  const handleOverrideChange = (questionId, field, value) => {
    setChanges(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (Object.keys(changes).length === 0) return;

    try {
      setSaving(true);

      // Build responses array with overrides
      const updatedResponses = [];
      Object.entries(changes).forEach(([questionId, change]) => {
        updatedResponses.push({
          questionId: parseInt(questionId),
          value: change.overrideValue || getResponseValue(questionId),
          advisorOverride: change.overrideValue ? {
            value: change.overrideValue,
            score: change.overrideScore
          } : null,
          advisorNotes: change.notes
        });
      });

      await saveQuestionnaireResponses(valuationId, updatedResponses);

      setChanges({});
      if (onSave) onSave();

      // Reload responses
      const data = await getQuestionnaireResponses(valuationId);
      setResponses(data);

      setSaving(false);
    } catch (err) {
      setError('Failed to save overrides');
      setSaving(false);
    }
  };

  const getResponseValue = (questionId) => {
    if (!responses) return null;
    for (const section of Object.values(responses.sections)) {
      const response = section.responses.find(r => r.questionId === questionId);
      if (response) return response.value;
    }
    return null;
  };

  const getRiskScoreImpact = () => {
    if (!responses) return null;

    let originalScore = responses.totalRiskScore;
    let modifiedScore = originalScore;

    // Calculate impact of changes
    Object.entries(changes).forEach(([questionId, change]) => {
      if (change.overrideScore !== undefined) {
        // Find original score for this question
        for (const section of Object.values(responses.sections)) {
          const response = section.responses.find(r => r.questionId === parseInt(questionId));
          if (response && response.score !== null) {
            modifiedScore = modifiedScore - response.score + change.overrideScore;
            break;
          }
        }
      }
    });

    if (modifiedScore !== originalScore) {
      return {
        original: originalScore,
        modified: modifiedScore,
        change: modifiedScore - originalScore
      };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="override-panel loading">
        <div className="spinner"></div>
        <p>Loading responses...</p>
      </div>
    );
  }

  if (error && !responses) {
    return (
      <div className="override-panel error">
        <p>{error}</p>
      </div>
    );
  }

  const hasChanges = Object.keys(changes).length > 0;
  const scoreImpact = getRiskScoreImpact();

  return (
    <div className="override-panel">
      <div className="panel-header">
        <h2>Review & Override Responses</h2>
        <p className="subtitle">
          Review client answers and apply advisor overrides where needed.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {/* Risk Score Summary */}
      <div className="risk-summary">
        <div className="risk-score-current">
          <span className="label">Current Risk Score</span>
          <span className="value">{responses?.totalRiskScore || 0} / 70</span>
        </div>
        {scoreImpact && (
          <div className="risk-score-modified">
            <span className="label">Modified Score</span>
            <span className="value">
              {scoreImpact.modified} / 70
              <span className={`change ${scoreImpact.change > 0 ? 'positive' : 'negative'}`}>
                ({scoreImpact.change > 0 ? '+' : ''}{scoreImpact.change})
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="sections-container">
        {responses?.sections && Object.entries(responses.sections).map(([sectionCode, section]) => (
          <div key={sectionCode} className="section">
            <button
              className={`section-header ${expandedSections[sectionCode] ? 'expanded' : ''}`}
              onClick={() => toggleSection(sectionCode)}
            >
              <span className="section-name">{section.name}</span>
              <span className="section-count">{section.responses.length} responses</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {expandedSections[sectionCode] && (
              <div className="section-content">
                {section.responses.map((response) => (
                  <div key={response.questionId} className="response-item">
                    <div className="response-header">
                      <span className="question-text">{response.questionText}</span>
                      {response.score !== null && (
                        <span className="original-score">Score: {response.score}</span>
                      )}
                    </div>

                    <div className="response-values">
                      <div className="client-response">
                        <span className="label">Client Answer:</span>
                        <span className="value">{response.valueLabel}</span>
                      </div>

                      {response.advisorOverride && (
                        <div className="existing-override">
                          <span className="label">Current Override:</span>
                          <span className="value">{response.advisorOverride.value}</span>
                        </div>
                      )}
                    </div>

                    <div className="override-controls">
                      <div className="override-field">
                        <label>Override Value:</label>
                        <input
                          type="text"
                          placeholder="Enter override value..."
                          value={changes[response.questionId]?.overrideValue || ''}
                          onChange={(e) => handleOverrideChange(response.questionId, 'overrideValue', e.target.value)}
                        />
                      </div>

                      {response.score !== null && (
                        <div className="override-field score">
                          <label>Override Score:</label>
                          <input
                            type="number"
                            min="-5"
                            max="5"
                            placeholder={response.score}
                            value={changes[response.questionId]?.overrideScore || ''}
                            onChange={(e) => handleOverrideChange(response.questionId, 'overrideScore', parseInt(e.target.value) || undefined)}
                          />
                        </div>
                      )}

                      <div className="override-field notes">
                        <label>Advisor Notes:</label>
                        <textarea
                          placeholder="Add notes about this override..."
                          value={changes[response.questionId]?.notes || response.advisorNotes || ''}
                          onChange={(e) => handleOverrideChange(response.questionId, 'notes', e.target.value)}
                          rows="2"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="panel-actions">
        {hasChanges && (
          <span className="changes-indicator">
            {Object.keys(changes).length} unsaved change(s)
          </span>
        )}
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Overrides'}
        </button>
      </div>
    </div>
  );
};

export default OverridePanel;
